<template>
    <!-- bo-api #931 (owner ruling 2026-10-15): an admin sets or changes an order's
         tracking number — PUT /api/v1/orders/tracking-attach/:id {tracking_number, replace}.
         Nothing is booked with the carrier. Replacing / a cancelled order asks first. -->
    <el-dialog
        :visible="visible"
        :title="$t('tracking_change.title', { id: orderId })"
        :append-to-body="true"
        :close-on-click-modal="false"
        width="520px"
        custom-class="bo-change-tracking-dialog"
        @update:visible="v => $emit('update:visible', v)"
    >
        <div data-testid="change-tracking">
            <div class="mb-3">
                <div class="form-control-label">{{ $t("tracking_change.current") }}</div>
                <div class="ct-current" data-testid="ct-current">{{ current || $t("tracking_change.none") }}</div>
            </div>
            <div v-if="plan.cancelled" class="alert alert-warning py-2 px-3 mb-3" data-testid="ct-cancelled-warning">
                <i class="fa fa-exclamation-triangle mr-1"></i>{{ $t("tracking_change.cancelled_warning") }}
            </div>
            <label class="form-control-label" for="ct-tn">{{ $t("tracking_change.new") }}</label>
            <el-input
                id="ct-tn"
                v-model="trackingNumber"
                maxlength="45"
                :class="{ 'ct-invalid': !!shownError }"
                data-testid="ct-tracking-number"
                @blur="touched = true"
                @keyup.enter.native="save"
            ></el-input>
            <div v-if="plan.replacing && !plan.same" class="ct-replace mt-1" data-testid="ct-replace-warning">
                {{ $t("tracking_change.replace_warning", { tn: current }) }}
            </div>
            <div class="text-muted ct-hint mt-1">{{ $t("tracking_change.hint") }}</div>
            <div v-if="shownError" class="ct-err mt-1" data-testid="ct-error">{{ $t(shownError) }}</div>
            <div v-if="serverError" class="alert alert-danger py-2 px-3 mt-3 mb-0" data-testid="ct-server-error">{{ serverError }}</div>
        </div>
        <span slot="footer" class="d-flex align-items-center">
            <!-- page-specific extras (e.g. Makro ship retry on the orders page) -->
            <slot name="footer-extra"></slot>
            <base-button class="ml-auto" type="secondary" :disabled="saving" @click="$emit('update:visible', false)">{{ $t("cancel") }}</base-button>
            <base-button type="primary" :disabled="saving" data-testid="ct-save" @click="save">
                <i v-if="saving" class="fa fa-spinner fa-spin mr-1"></i>{{ $t("save") }}
            </base-button>
        </span>
    </el-dialog>
</template>

<script>
import { Dialog, Input } from "element-ui";
import swal from "sweetalert2";
import { OrderService } from "@/resource";
import { packingRoundErrorKey } from "@/utils/sellerPackingRound";
import {
    currentTrackingOf,
    shouldAskReplace,
    trackingChangeConfirmKeys,
    trackingChangeErrorKey,
    trackingChangePayload,
    trackingChangePlan
} from "@/utils/trackingChange";

export default {
    name: "ChangeTrackingDialog",
    components: {
        [Dialog.name]: Dialog,
        [Input.name]: Input
    },
    props: {
        visible: { type: Boolean, default: false },
        // An order row: ID (or id), shipping_tracking_number, order_status.
        order: { type: Object, default: null }
    },
    data() {
        return { trackingNumber: "", touched: false, saving: false, serverError: "" };
    },
    computed: {
        orderId() {
            const o = this.order || {};
            return Number(o.ID || o.id || 0);
        },
        current() {
            return currentTrackingOf(this.order);
        },
        plan() {
            return trackingChangePlan(this.order, this.trackingNumber);
        },
        shownError() {
            if (!this.touched) return "";
            if (this.plan.error) return this.plan.error;
            return this.plan.same ? "tracking_change.same" : "";
        }
    },
    watch: {
        visible: {
            immediate: true,
            handler(v) {
                if (!v) return;
                this.trackingNumber = "";
                this.touched = false;
                this.serverError = "";
            }
        }
    },
    methods: {
        async confirm(keys, params) {
            const text = keys.map(k => this.$t(k, params)).join("\n");
            const res = await swal.fire({
                icon: "warning",
                title: this.$t("tracking_change.confirm_title"),
                text,
                showCancelButton: true,
                confirmButtonText: this.$t("confirm"),
                cancelButtonText: this.$t("cancel")
            });
            return !!(res && res.value);
        },
        async save() {
            this.touched = true;
            const plan = this.plan;
            if (plan.error || plan.same || !this.orderId) return;
            const params = { old: plan.current, new: plan.tn };
            const keys = trackingChangeConfirmKeys(plan);
            if (keys.length && !(await this.confirm(keys, params))) return;
            await this.submit(plan, false, params);
        },
        async submit(plan, forceReplace, params) {
            const body = trackingChangePayload(plan, forceReplace);
            this.saving = true;
            this.serverError = "";
            try {
                const res = await OrderService.attachTracking(this.orderId, body);
                const data = (res && res.data && res.data.data) || {};
                this.$notify({ type: "success", message: this.$t("tracking_change.saved", { tn: plan.tn }) });
                this.$emit("saved", {
                    id: this.orderId,
                    tracking_number: data.shipping_tracking_number || plan.tn,
                    previous_tracking_number: data.previous_tracking_number != null ? data.previous_tracking_number : plan.current
                });
                this.$emit("update:visible", false);
            } catch (err) {
                const key = packingRoundErrorKey(err);
                if (shouldAskReplace(key, body.replace)) {
                    // The order got a number meanwhile: confirm the overwrite, resend.
                    this.saving = false;
                    if (await this.confirm(["tracking_change.has_tracking_now"], params)) {
                        await this.submit(plan, true, params);
                    }
                    return;
                }
                const k = trackingChangeErrorKey(key);
                this.serverError = k && this.$te(k) ? this.$t(k) : ((err && err.message) || this.$t("errors.internal"));
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style>
.bo-change-tracking-dialog .ct-current {
    font-family: SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 1rem;
    word-break: break-all;
}
.bo-change-tracking-dialog .ct-hint,
.bo-change-tracking-dialog .ct-replace {
    font-size: 0.8125rem;
}
.bo-change-tracking-dialog .ct-replace {
    color: #8a5a00;
}
.bo-change-tracking-dialog .ct-err {
    color: #c0392b;
    font-size: 0.8125rem;
}
.bo-change-tracking-dialog .ct-invalid .el-input__inner {
    border-color: #c0392b;
}
</style>
